import { useState, type FormEvent, type ReactNode } from "react";
import { CustomerReports } from "../help/Help";
import { CustomerStatus } from "./CustomerStatus";
import { CustomerDocumentsCard, CustomerOwnershipCard } from "./CustomerOwnership";
import { CustomerPlan, CustomerRequests, REQUEST_LABEL, REQUEST_TONE, UrgencyTag } from "./CustomerServices";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader, plural } from "../components/PageHeader";
import { EmptyState, ErrorState, Skeleton, SkeletonLines } from "../components/States";
import { BRAND } from "../lib/config";
import {
  answerFeedback,
  changeLogin,
  createCustomer,
  deleteMilestone,
  deleteRoadmapItem,
  getCustomerDetail,
  inviteLogin,
  listCustomers,
  loginLink,
  openCustomerFile,
  saveMilestone,
  saveProject,
  saveRoadmapItem,
  type CustomerDetail,
  type CustomerRole,
  type FeedbackStatus,
  type MilestoneFields,
  type MilestoneStatus,
  type ProjectFields,
  type StaffFeedback,
  type StaffMilestone,
  type StaffProject,
  type StaffRoadmapItem,
} from "../lib/customers-data";
import { refreshCounts } from "../lib/events";
import { fmtDate, fmtPlainDate, humanise } from "../lib/format";
import { hashParams, useHash } from "../lib/route";
import { useAsync } from "../lib/useAsync";

const STAGES = BRAND.customerStages;
const stageLabel = (key: string) => STAGES.find((s) => s.key === key)?.label ?? key;
const MILESTONE_LABEL: Record<MilestoneStatus, string> = { not_started: "Not started", in_progress: "In progress", ready_for_review: "Ready for review", approved: "Approved" };
const MILESTONE_TONE: Record<MilestoneStatus, string> = { not_started: "outline", in_progress: "grey", ready_for_review: "accent", approved: "green" };
const FEEDBACK_LABEL: Record<FeedbackStatus, string> = { received: "Received", in_progress: "In progress", done: "Done" };
const FEEDBACK_TONE: Record<FeedbackStatus, string> = { received: "accent", in_progress: "grey", done: "green" };
const ROLE_LABEL: Record<CustomerRole, string> = { owner: "Owner (everything, approves milestones)", member: "Member (view and feedback)", accounts: "Accounts (invoices and payments)" };
const EVENT_LABEL: Record<string, string> = {
  sign_in: "Signed in",
  demo_sign_in: "Demo sign-in",
  milestone_approved: "Approved a milestone",
  milestone_sent_back: "Sent a milestone back",
  feedback_created: "Sent feedback",
  user_invited: "Login created",
  user_changed: "Login changed",
  request_created: "Raised a request",
  request_replied: "Replied on a request",
  addon_quote_created: "Chose an add-on (quote made)",
  quote_sent: "Quote sent",
  quote_accepted: "Accepted a quote",
  quote_declined: "Declined a quote",
  invite_cancelled: "Cancelled an invitation",
  user_removed: "Removed a colleague",
  notifications_changed: "Changed email settings",
  handover_pack_made: "Made the handover pack",
  document_uploaded: "Document shared",
  document_removed: "Document removed",
  document_downloaded: "Downloaded a document",
};

const nullIfBlank = (s: string) => (s.trim() ? s.trim() : null);

/** #/customers (customers and feedback to answer) and #/customers/<id> (one customer's portal). */
export function Customers() {
  const [id] = hashParams(useHash());
  return id ? <CustomerView key={id} id={id} /> : <CustomerList />;
}

function CustomerList() {
  const { data, error, loading, reload } = useAsync(listCustomers);
  return (
    <section className="page stack" aria-labelledby="page-title">
      <PageHeader eyebrow="Customer portal" title="Customers" meta="Each customer's projects, portal logins, feedback and requests. Customers sign in to their own portal." />
      {loading && !data && <Skeleton columns={4} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.openRequests.length > 0 && (
        <section className="card card--alert" aria-labelledby="requests-todo-title">
          <div className="card__head"><h2 className="card__title" id="requests-todo-title">Requests to answer</h2></div>
          <ul className="list">
            {data.openRequests.map((r) => (
              <li key={r.id} className="list__row">
                <span>
                  <a href={`#/customers/${r.customer_id}`}><strong>{r.customer}</strong></a>: {r.title}
                  <UrgencyTag urgency={r.urgency} />
                  <span className="sub">{r.reference}, {r.author_name}, {fmtDate(r.created_at)}</span>
                </span>
                <span className={`chip chip--${REQUEST_TONE[r.status]}`}>{REQUEST_LABEL[r.status]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.openFeedback.length > 0 && (
        <section className="card card--alert" aria-labelledby="todo-title">
          <div className="card__head"><h2 className="card__title" id="todo-title">Feedback to answer</h2></div>
          <ul className="list">
            {data.openFeedback.map((f) => (
              <li key={f.id} className="list__row">
                <span>
                  <a href={`#/customers/${f.customer_id}`}><strong>{f.customer}</strong></a>: {f.body.length > 140 ? `${f.body.slice(0, 140)}...` : f.body}
                  <span className="sub">{f.author_name}, {fmtDate(f.created_at)}</span>
                </span>
                <span className={`chip chip--${FEEDBACK_TONE[f.status]}`}>{FEEDBACK_LABEL[f.status]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.customers.length === 0 && <EmptyState help icon="users" title="No customers yet">Add a customer below, then give them a project and a portal login.</EmptyState>}
      {data && data.customers.length > 0 && (
        <div className="table-wrap table-wrap--flat">
          <table className="table table--stack">
            <thead>
              <tr><th scope="col">Customer</th><th scope="col">Projects</th><th scope="col" className="n">Logins</th><th scope="col" className="n">Open feedback</th>{__OPS_PORTAL__.customerRequests && <th scope="col" className="n">Open requests</th>}</tr>
            </thead>
            <tbody>
              {data.customers.map((c) => (
                <tr key={c.id}>
                  <td data-label="Customer"><a href={`#/customers/${c.id}`}><strong>{c.name}</strong></a>{c.contact_name && <span className="sub">{c.contact_name}</span>}</td>
                  <td data-label="Projects">
                    {c.projects.filter((p) => !p.archived).map((p) => <span key={p.id} className="sub">{p.name}: {stageLabel(p.stage)}</span>)}
                    {!c.projects.some((p) => !p.archived) && <span className="muted">None</span>}
                  </td>
                  <td data-label="Logins" className="n">{c.logins}</td>
                  <td data-label="Open feedback" className="n">{c.openFeedback > 0 ? <strong>{c.openFeedback}</strong> : 0}</td>
                  {__OPS_PORTAL__.customerRequests && <td data-label="Open requests" className="n">{c.openRequests > 0 ? <strong>{c.openRequests}</strong> : 0}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewCustomer onCreated={(id) => { window.location.hash = `#/customers/${id}`; }} />
    </section>
  );
}

function NewCustomer({ onCreated }: { onCreated: (id: string) => void }) {
  const { notice, setNotice, fail } = useNotice();
  const [f, setF] = useState({ name: "", abn: "", contact_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      onCreated(await createCustomer({ name: f.name.trim(), abn: nullIfBlank(f.abn.replace(/\s/g, "")), contact_name: nullIfBlank(f.contact_name), email: nullIfBlank(f.email), phone: null }));
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }
  return (
    <details className="card cs-details">
      <summary className="card__head"><span className="card__title">Add a customer</span><Icon name="plus" size={20} /></summary>
      <form className="card__body stack" onSubmit={submit}>
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <div className="cs-grid">
          <Field label="Business name"><input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="ABN (optional)"><input className="input" inputMode="numeric" pattern="[0-9 ]{11,14}" value={f.abn} onChange={(e) => setF({ ...f, abn: e.target.value })} /></Field>
          <Field label="Main contact (optional)"><input className="input" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></Field>
          <Field label="Email (optional)"><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        </div>
        <div><button type="submit" className="btn btn--primary btn--sm" disabled={busy}>{busy ? "Adding" : "Add customer"}</button></div>
      </form>
    </details>
  );
}

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`field${wide ? " cs-wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

function CustomerView({ id }: { id: string }) {
  const { data, error, loading, reload } = useAsync(() => getCustomerDetail(id), [id]);
  const { notice, setNotice, fail, ok } = useNotice();
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setNotice(null);
    try {
      await fn();
      if (done) ok(done);
      reload();
      refreshCounts();
    } catch (e) {
      fail(e);
    }
  };

  if (loading && !data) return <section className="page"><SkeletonLines rows={6} /></section>;
  if (error) return <section className="page"><ErrorState message={error} onRetry={reload} /></section>;
  if (!data) return <section className="page"><EmptyState title="Customer not found"><a href="#/customers">Back to customers</a></EmptyState></section>;

  return (
    <section className="page stack" aria-labelledby="page-title">
      <a href="#/customers" className="cs-back"><Icon name="collapse" size={18} /> Customers</a>
      <PageHeader eyebrow="Customer" title={data.customer.name} meta={[data.customer.contact_name, data.customer.email, data.customer.abn && `ABN ${data.customer.abn}`].filter(Boolean).join(", ") || undefined} />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <CustomerRequests data={data} act={act} />
      <Feedback data={data} act={act} />
      <CustomerReports customerId={data.customer.id} />
      {data.projects.map((p) => <ProjectCard key={p.id} data={data} project={p} act={act} />)}
      <ProjectForm customerId={data.customer.id} project={null} act={act} />
      <CustomerPlan data={data} act={act} />
      {__OPS_PORTAL__.customerStatus && <CustomerStatus data={data} act={act} />}
      <CustomerOwnershipCard data={data} act={act} />
      <CustomerDocumentsCard data={data} act={act} />
      <Logins data={data} act={act} />
      <Activity data={data} />
    </section>
  );
}

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<void>;

/* ------------------------------------------------------------------ logins */

function Logins({ data, act }: { data: CustomerDetail; act: Act }) {
  const cid = data.customer.id;
  const [f, setF] = useState({ email: "", fullName: "", position: "", role: "owner" as CustomerRole, sendEmail: true });
  const [link, setLink] = useState<{ id: string; url: string } | null>(null);
  async function invite(e: FormEvent) {
    e.preventDefault();
    await act(async () => {
      const r = await inviteLogin(cid, { email: f.email.trim(), fullName: f.fullName.trim(), position: f.position.trim() || undefined, role: f.role, sendEmail: f.sendEmail });
      setF({ email: "", fullName: "", position: "", role: "member", sendEmail: true });
      return r;
    }, f.sendEmail ? `Login ready. A sign-in link is on its way to ${f.email.trim()}.` : "Login ready.");
  }
  return (
    <section className="card" aria-labelledby="logins-title">
      <div className="card__head"><h2 className="card__title" id="logins-title">Portal logins</h2></div>
      {data.logins.length === 0 ? (
        <div className="card__body"><p className="muted">No logins yet. Add the customer's main contact as an owner.</p></div>
      ) : (
        <ul className="list">
          {data.logins.map((l) => (
            <li key={l.id} className="list__row cs-login">
              <span>
                <strong>{l.full_name || l.email}</strong>{!l.active && <span className="chip chip--outline cs-gap">Turned off</span>}
                <span className="sub">{l.email}{l.position ? `, ${l.position}` : ""}</span>
                <span className="sub">{l.last_sign_in_at ? `Last signed in ${fmtDate(l.last_sign_in_at)}` : "Not signed in yet"}</span>
                {link?.id === l.id && <span className="sub cs-link">One-time sign-in link: <input className="input input--compact" readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Sign-in link" /></span>}
              </span>
              <span className="cs-login__actions">
                <select className="input input--compact" aria-label={`Role for ${l.email}`} value={l.role} onChange={(e) => act(() => changeLogin(cid, l.id, { role: e.target.value as CustomerRole }))}>
                  {(Object.keys(ROLE_LABEL) as CustomerRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r].split(" (")[0]}</option>)}
                </select>
                {l.active && <button type="button" className="btn btn--outline btn--xs" onClick={() => act(async () => setLink({ id: l.id, url: await loginLink(cid, l.id) }))}>Sign-in link</button>}
                <button type="button" className="btn btn--outline btn--xs" onClick={() => act(() => changeLogin(cid, l.id, { active: !l.active }), l.active ? "Login turned off." : "Login turned back on.")}>
                  {l.active ? "Turn off" : "Turn on"}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="card__body"><p className="small muted">Owners can also invite colleagues, change roles and remove people under Team and settings in their portal. The portal never lets them remove or demote their last owner; you can (for example when the only owner leaves), so add another owner first where you can.</p></div>
      <details className="cs-details cs-details--inner">
        <summary className="cs-summary"><Icon name="plus" size={18} /> Add a login</summary>
        <form className="card__body stack" onSubmit={invite}>
          <div className="cs-grid">
            <Field label="Full name"><input className="input" required minLength={2} value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></Field>
            <Field label="Email"><input className="input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Position (optional)"><input className="input" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} placeholder="Shown on sign-offs" /></Field>
            <Field label="Role">
              <select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as CustomerRole })}>
                {(Object.keys(ROLE_LABEL) as CustomerRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </Field>
          </div>
          <label className="cs-check"><input type="checkbox" checked={f.sendEmail} onChange={(e) => setF({ ...f, sendEmail: e.target.checked })} /> Email them a sign-in link now</label>
          <div><button type="submit" className="btn btn--primary btn--sm">Add login</button></div>
        </form>
      </details>
    </section>
  );
}

/* ------------------------------------------------------------------ projects */

function ProjectForm({ customerId, project, act }: { customerId: string; project: StaffProject | null; act: Act }) {
  const [f, setF] = useState<ProjectFields>({
    name: project?.name ?? "",
    summary: project?.summary ?? "",
    stage: project?.stage ?? STAGES[0]?.key ?? "",
    staging_url: project?.staging_url ?? "",
    live_url: project?.live_url ?? "",
    started_on: project?.started_on ?? "",
    target_launch: project?.target_launch ?? "",
    archived: project?.archived ?? false,
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    await act(
      () => saveProject(customerId, project?.id ?? null, {
        ...f,
        name: f.name.trim(),
        summary: nullIfBlank(f.summary ?? ""),
        staging_url: nullIfBlank(f.staging_url ?? ""),
        live_url: nullIfBlank(f.live_url ?? ""),
        started_on: f.started_on || null,
        target_launch: f.target_launch || null,
      }),
      project ? "Project saved." : "Project added.",
    );
  }
  const body = (
    <form className="card__body stack" onSubmit={submit}>
      <div className="cs-grid">
        <Field label="Project name"><input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Current stage">
          <select className="input" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Summary (shown to the customer)" wide><textarea className="input cs-textarea" rows={2} value={f.summary ?? ""} onChange={(e) => setF({ ...f, summary: e.target.value })} /></Field>
        <Field label="Preview site (https)"><input className="input" type="url" pattern="https://.*" value={f.staging_url ?? ""} onChange={(e) => setF({ ...f, staging_url: e.target.value })} /></Field>
        <Field label="Live site (https)"><input className="input" type="url" pattern="https://.*" value={f.live_url ?? ""} onChange={(e) => setF({ ...f, live_url: e.target.value })} /></Field>
        <Field label="Started"><input className="input" type="date" value={f.started_on ?? ""} onChange={(e) => setF({ ...f, started_on: e.target.value })} /></Field>
        <Field label="Target launch"><input className="input" type="date" value={f.target_launch ?? ""} onChange={(e) => setF({ ...f, target_launch: e.target.value })} /></Field>
      </div>
      {project && <label className="cs-check"><input type="checkbox" checked={f.archived} onChange={(e) => setF({ ...f, archived: e.target.checked })} /> Archived (hidden from the customer)</label>}
      <div><button type="submit" className="btn btn--primary btn--sm">{project ? "Save project" : "Add project"}</button></div>
    </form>
  );
  if (project) return body;
  return (
    <details className="card cs-details">
      <summary className="card__head"><span className="card__title">Add a project</span><Icon name="plus" size={20} /></summary>
      {body}
    </details>
  );
}

function ProjectCard({ data, project, act }: { data: CustomerDetail; project: StaffProject; act: Act }) {
  const milestones = data.milestones.filter((m) => m.project_id === project.id);
  const roadmap = data.roadmap.filter((r) => r.project_id === project.id);
  const nextPosition = (list: { position: number }[]) => (list.length ? Math.max(...list.map((x) => x.position)) + 1 : 1);
  return (
    <section className="card" aria-labelledby={`proj-${project.id}`}>
      <div className="card__head">
        <h2 className="card__title" id={`proj-${project.id}`}>{project.name}</h2>
        <span className="chip chip--ink">{stageLabel(project.stage)}</span>
      </div>
      <div className="card__body stack">
        {project.archived && <p className="muted">Archived: the customer does not see this project.</p>}
        <p className="small">
          {project.staging_url ? <>Preview: <a href={project.staging_url} target="_blank" rel="noopener noreferrer">{project.staging_url}</a></> : <span className="muted">No preview link yet.</span>}
          {project.target_launch && <> Target launch {fmtPlainDate(project.target_launch)}.</>}
        </p>
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary">Edit project details</summary>
          <ProjectForm customerId={data.customer.id} project={project} act={act} />
        </details>

        <h3 className="cs-h3">Milestones</h3>
        {milestones.length === 0 && <p className="muted">No milestones yet.</p>}
        <ol className="cs-milestones">
          {milestones.map((m) => <MilestoneRow key={m.id} data={data} milestone={m} act={act} />)}
        </ol>
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary"><Icon name="plus" size={18} /> Add a milestone</summary>
          <MilestoneForm customerId={data.customer.id} projectId={project.id} milestone={null} position={nextPosition(milestones)} act={act} />
        </details>

        <h3 className="cs-h3">What's next</h3>
        {roadmap.length === 0 && <p className="muted">Nothing queued after the current work.</p>}
        <ul className="list cs-roadmap">
          {roadmap.map((r) => (
            <li key={r.id} className="list__row">
              <span><strong>{r.title}</strong>{r.timing && <span className="sub">{r.timing}</span>}{r.description && <span className="sub">{r.description}</span>}</span>
              <span className="cs-login__actions">
                <select className="input input--compact" aria-label={`Status of ${r.title}`} value={r.status} onChange={(e) => act(() => saveRoadmapItem(data.customer.id, project.id, r.id, { ...r, status: e.target.value as StaffRoadmapItem["status"] }))}>
                  <option value="planned">Planned</option><option value="in_progress">In progress</option><option value="done">Done</option>
                </select>
                <button type="button" className="btn btn--outline btn--xs" onClick={() => window.confirm(`Remove "${r.title}"?`) && act(() => deleteRoadmapItem(r.id))}>Remove</button>
              </span>
            </li>
          ))}
        </ul>
        <RoadmapForm customerId={data.customer.id} projectId={project.id} position={nextPosition(roadmap)} act={act} />
      </div>
    </section>
  );
}

function MilestoneRow({ data, milestone: m, act }: { data: CustomerDetail; milestone: StaffMilestone; act: Act }) {
  const reviews = data.reviews.filter((r) => r.milestone_id === m.id);
  return (
    <li className="cs-milestone">
      <div className="cs-milestone__head">
        <span><strong>{m.position}. {m.title}</strong><span className="sub">{m.percent_complete}% complete{m.target_date ? `, target ${fmtPlainDate(m.target_date)}` : ""}</span></span>
        <span className={`chip chip--${MILESTONE_TONE[m.status]}`}>{MILESTONE_LABEL[m.status]}</span>
      </div>
      {reviews.map((r) => (
        <p key={r.id} className="small cs-review">
          <strong>{r.decision === "approved" ? "Approved" : "Sent back"}</strong> by {r.reviewer_name}, {fmtDate(r.created_at)}{r.comments ? `: ${r.comments}` : ""}
        </p>
      ))}
      {m.status === "approved" ? (
        <p className="small muted">Approved by the customer{m.approved_at ? ` on ${fmtDate(m.approved_at)}` : ""}. Approved milestones are locked.</p>
      ) : (
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary">Edit milestone</summary>
          <MilestoneForm customerId={data.customer.id} projectId={m.project_id} milestone={m} position={m.position} act={act} />
        </details>
      )}
    </li>
  );
}

function MilestoneForm({ customerId, projectId, milestone, position, act }: { customerId: string; projectId: string; milestone: StaffMilestone | null; position: number; act: Act }) {
  const [f, setF] = useState<MilestoneFields>({
    title: milestone?.title ?? "",
    description: milestone?.description ?? "",
    target_date: milestone?.target_date ?? "",
    status: milestone?.status ?? "not_started",
    percent_complete: milestone?.percent_complete ?? 0,
    review_notes: milestone?.review_notes ?? "",
    position,
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    const fields = { ...f, title: f.title.trim(), description: nullIfBlank(f.description ?? ""), review_notes: nullIfBlank(f.review_notes ?? ""), target_date: f.target_date || null };
    await act(
      () => saveMilestone(customerId, projectId, milestone?.id ?? null, fields),
      f.status === "ready_for_review" && milestone?.status !== "ready_for_review" ? "Saved. The customer now sees it as ready for their review." : "Milestone saved.",
    );
  }
  return (
    <form className="card__body stack" onSubmit={submit}>
      <div className="cs-grid">
        <Field label="Title"><input className="input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Status">
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as MilestoneStatus })}>
            {(["not_started", "in_progress", "ready_for_review"] as const).map((s) => <option key={s} value={s}>{MILESTONE_LABEL[s]}</option>)}
          </select>
        </Field>
        <Field label="Description (shown to the customer)" wide><textarea className="input cs-textarea" rows={2} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Target date"><input className="input" type="date" value={f.target_date ?? ""} onChange={(e) => setF({ ...f, target_date: e.target.value })} /></Field>
        <Field label="Percent complete"><input className="input" type="number" min={0} max={100} required value={f.percent_complete} onChange={(e) => setF({ ...f, percent_complete: Number(e.target.value) })} /></Field>
        <Field label="Order"><input className="input" type="number" min={0} required value={f.position} onChange={(e) => setF({ ...f, position: Number(e.target.value) })} /></Field>
        <Field label="What's new for this review (shown with the preview link)" wide><textarea className="input cs-textarea" rows={3} value={f.review_notes ?? ""} onChange={(e) => setF({ ...f, review_notes: e.target.value })} /></Field>
      </div>
      <p className="small muted">Set the status to "Ready for review" when the customer should check the preview and approve. Only the customer can approve.</p>
      <div className="cs-actions">
        <button type="submit" className="btn btn--primary btn--sm">{milestone ? "Save milestone" : "Add milestone"}</button>
        {milestone && (
          <button type="button" className="btn btn--outline btn--sm" onClick={() => window.confirm(`Delete "${milestone.title}"? Milestones with a review history cannot be deleted.`) && act(() => deleteMilestone(milestone.id), "Milestone deleted.")}>
            Delete
          </button>
        )}
      </div>
    </form>
  );
}

function RoadmapForm({ customerId, projectId, position, act }: { customerId: string; projectId: string; position: number; act: Act }) {
  const [f, setF] = useState({ title: "", timing: "", description: "" });
  async function submit(e: FormEvent) {
    e.preventDefault();
    await act(async () => {
      await saveRoadmapItem(customerId, projectId, null, { title: f.title.trim(), timing: nullIfBlank(f.timing), description: nullIfBlank(f.description), status: "planned", position });
      setF({ title: "", timing: "", description: "" });
    });
  }
  return (
    <details className="cs-details cs-details--inner">
      <summary className="cs-summary"><Icon name="plus" size={18} /> Add to what's next</summary>
      <form className="card__body stack" onSubmit={submit}>
        <div className="cs-grid">
          <Field label="Title"><input className="input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Timing (optional)"><input className="input" value={f.timing} placeholder="For example, Early 2027" onChange={(e) => setF({ ...f, timing: e.target.value })} /></Field>
          <Field label="Description (optional)" wide><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        </div>
        <div><button type="submit" className="btn btn--primary btn--sm">Add</button></div>
      </form>
    </details>
  );
}

/* ------------------------------------------------------------------ feedback */

function Feedback({ data, act }: { data: CustomerDetail; act: Act }) {
  if (data.feedback.length === 0) return null;
  const open = data.feedback.filter((f) => f.status !== "done");
  return (
    <section className={`card${open.length ? " card--alert" : ""}`} aria-labelledby="fb-title">
      <div className="card__head">
        <h2 className="card__title" id="fb-title">Feedback</h2>
        <span className="muted small">{plural(open.length, "open item")}</span>
      </div>
      <ul className="list">
        {data.feedback.map((f) => <FeedbackRow key={f.id} data={data} item={f} act={act} />)}
      </ul>
    </section>
  );
}

function FeedbackRow({ data, item: f, act }: { data: CustomerDetail; item: StaffFeedback; act: Act }) {
  const [reply, setReply] = useState(f.reply ?? "");
  const milestone = f.milestone_id ? data.milestones.find((m) => m.id === f.milestone_id) : null;
  const project = data.projects.find((p) => p.id === f.project_id);
  return (
    <li className="list__row cs-feedback">
      <div className="cs-feedback__main">
        <p className="cs-pre">{f.body}</p>
        <span className="sub">
          {f.author_name}, {fmtDate(f.created_at)}. {project?.name}{milestone ? `, ${milestone.title}` : ""}
        </span>
        {f.page_url && <a className="sub sub--link cs-break" href={f.page_url} target="_blank" rel="noopener noreferrer">{f.page_url}</a>}
        {f.feedback_files.length > 0 && (
          <span className="cs-files">
            {f.feedback_files.map((file) => (
              <button key={file.id} type="button" className="btn btn--outline btn--xs" onClick={() => act(() => openCustomerFile(file.id))}>
                <Icon name="file" size={16} /> {file.file_name}
              </button>
            ))}
          </span>
        )}
        <form className="cs-reply" onSubmit={(e) => { e.preventDefault(); void act(() => answerFeedback(f.id, { reply: nullIfBlank(reply), status: f.status === "received" && reply.trim() ? "in_progress" : f.status }), "Reply saved. The customer sees it in their portal."); }}>
          <label className="field">
            <span className="field__label">Reply to the customer</span>
            <textarea className="input cs-textarea" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} />
          </label>
          {f.replied_at && <span className="sub">Replied by {f.replied_by_name ?? "staff"}, {fmtDate(f.replied_at)}</span>}
          <div><button type="submit" className="btn btn--outline btn--xs" disabled={reply.trim() === (f.reply ?? "")}>Save reply</button></div>
        </form>
      </div>
      <select className={`status-select chip--${FEEDBACK_TONE[f.status]}`} aria-label="Feedback status" value={f.status} onChange={(e) => act(() => answerFeedback(f.id, { status: e.target.value as FeedbackStatus }))}>
        {(Object.keys(FEEDBACK_LABEL) as FeedbackStatus[]).map((s) => <option key={s} value={s}>{FEEDBACK_LABEL[s]}</option>)}
      </select>
    </li>
  );
}

/* ------------------------------------------------------------------ activity */

function Activity({ data }: { data: CustomerDetail }) {
  if (data.events.length === 0) return null;
  const who = (id: string | null) => data.logins.find((l) => l.id === id)?.full_name || data.logins.find((l) => l.id === id)?.email || "";
  return (
    <section className="card" aria-labelledby="activity-title">
      <div className="card__head"><h2 className="card__title" id="activity-title">Portal activity</h2><span className="muted small">Latest 30</span></div>
      <ul className="list">
        {data.events.map((e) => (
          <li key={e.id} className="list__row">
            <span>{e.action === "user_invited" && e.detail.by === "customer" ? "Invited a colleague" : e.action === "user_changed" && e.detail.by === "customer" ? "Changed a colleague's role" : (EVENT_LABEL[e.action] ?? humanise(e.action))}{e.user_id && who(e.user_id) ? `: ${who(e.user_id)}` : ""}{e.staff_id ? " (by staff)" : ""}</span>
            <span className="list__value list__value--muted small">{fmtDate(e.created_at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
